/**
 * Who may edit a time-off request, and what the edit does to its approval
 * state. Single source of truth shared by the server (PUT-route enforcement)
 * and the client (edit-button visibility + the "goes back for approval"
 * confirm) so the two can never disagree.
 *
 * `canManageTarget` must be the actor's ROLE-DERIVED manage rights over the
 * request's owner — the permission resolver's self-baseline (which grants
 * everyone `canManage` on themselves) must NOT be fed in here, or every
 * employee could edit their own concluded leave.
 */

export type TimeOffEditorRole = 'self' | 'manager'

export type TimeOffEditDenialReason = 'STATUS_NOT_EDITABLE' | 'NOT_AUTHORIZED'

export interface TimeOffEditAccessInput {
    isSelf: boolean
    canManageTarget: boolean
    status: string
    /** Request end date, YYYY-MM-DD inclusive. */
    endDate: string
    /** YYYY-MM-DD. */
    today: string
}

export type TimeOffEditAccess =
    | { allowed: true; editorRole: TimeOffEditorRole }
    | { allowed: false; reason: TimeOffEditDenialReason }

/**
 * A live (ongoing or future) request is editable by its owner — as 'self',
 * even when the owner also holds manage rights, so the approval flow re-runs
 * exactly like POST puts an admin's own bookings into PENDING. A concluded
 * request (ended before today) is editable with manage rights in place (as
 * 'manager'), and by its owner as 'self' — a retroactive owner edit of an
 * approval-flow request goes back to the manager exactly like a live one
 * (TASK-402).
 */
export const resolveTimeOffEditAccess = (input: TimeOffEditAccessInput): TimeOffEditAccess => {
    if (input.status !== 'PENDING' && input.status !== 'APPROVED') {
        return { allowed: false, reason: 'STATUS_NOT_EDITABLE' }
    }

    const concluded = input.endDate < input.today
    if (concluded) {
        // Manage rights first: an admin fixing their own past leave applies it
        // in place instead of sending it to an approver.
        if (input.canManageTarget) return { allowed: true, editorRole: 'manager' }
        if (input.isSelf) return { allowed: true, editorRole: 'self' }
        return { allowed: false, reason: 'NOT_AUTHORIZED' }
    }

    if (input.isSelf) return { allowed: true, editorRole: 'self' }
    if (input.canManageTarget) return { allowed: true, editorRole: 'manager' }
    return { allowed: false, reason: 'NOT_AUTHORIZED' }
}

export type TimeOffCancelDenialReason =
    | 'STATUS_NOT_CANCELLABLE'
    | 'CANCEL_ALREADY_REQUESTED'
    | 'NOT_AUTHORIZED'

export interface TimeOffCancelAccessInput {
    isSelf: boolean
    /** Role-derived manage rights — same contract as TimeOffEditAccessInput. */
    canManageTarget: boolean
    status: string
    /** Request start date, YYYY-MM-DD. */
    startDate: string
    /** YYYY-MM-DD. */
    today: string
    /** Whether the request's policy requires manager approval. */
    requiresApproval: boolean
    /** Whether a cancellation is already awaiting the manager's decision. */
    cancelRequested: boolean
}

export type TimeOffCancelAccess =
    | { allowed: true; mode: 'direct'; actorRole: TimeOffEditorRole }
    | { allowed: true; mode: 'requestApproval' }
    | { allowed: false; reason: TimeOffCancelDenialReason }

/**
 * Who may cancel (delete) a time-off request, and whether the cancellation
 * applies directly or must go to the manager first. Shared by the server
 * (PATCH cancel enforcement) and the client (cancel-button visibility + the
 * "goes for approval" confirm) — same contract as resolveTimeOffEditAccess.
 *
 * Manage rights always cancel in place — including a started or concluded
 * absence (the ledger deduction is refunded). The owner cancels their own
 * PENDING request or a not-yet-started APPROVED one directly; a retroactive
 * owner cancellation of a started/concluded APPROVED absence runs through the
 * approval flow when the policy requires approval (TASK-402).
 */
export const resolveTimeOffCancelAccess = (
    input: TimeOffCancelAccessInput,
): TimeOffCancelAccess => {
    if (input.status !== 'PENDING' && input.status !== 'APPROVED') {
        return { allowed: false, reason: 'STATUS_NOT_CANCELLABLE' }
    }

    // Manage rights cancel in place — a flagged (cancel-requested) row too:
    // that direct cancel IS the approval of the owner's request.
    if (input.canManageTarget) return { allowed: true, mode: 'direct', actorRole: 'manager' }

    if (!input.isSelf) return { allowed: false, reason: 'NOT_AUTHORIZED' }

    if (input.cancelRequested) return { allowed: false, reason: 'CANCEL_ALREADY_REQUESTED' }

    // Own PENDING request: nothing was approved yet, withdraw freely.
    if (input.status === 'PENDING') return { allowed: true, mode: 'direct', actorRole: 'self' }

    // Own upcoming APPROVED absence: direct, as before.
    if (input.startDate > input.today) return { allowed: true, mode: 'direct', actorRole: 'self' }

    // Own started/concluded APPROVED absence: refunding consumed days needs
    // the manager's sign-off when the policy has an approval flow.
    if (input.requiresApproval) return { allowed: true, mode: 'requestApproval' }
    return { allowed: true, mode: 'direct', actorRole: 'self' }
}

export type TimeOffEditOutcome = 'apply' | 'stayPending' | 'revertToPending'

export interface TimeOffEditOutcomeInput {
    editorRole: TimeOffEditorRole
    status: 'PENDING' | 'APPROVED'
    /** Whether the NEW policy (after the edit) requires manager approval. */
    requiresApproval: boolean
    datesChanged: boolean
    /** Whether the edit changes the leave type/policy. */
    policyChanged: boolean
}

/**
 * What the edit does to the request's approval state. An owner changing the
 * dates OR the type of an approved approval-flow request sends it back to the
 * manager ('revertToPending'); note-only edits, no-approval policies and
 * manager edits (the editor IS the approver) apply in place.
 */
export const resolveTimeOffEditOutcome = (input: TimeOffEditOutcomeInput): TimeOffEditOutcome => {
    if (input.status === 'PENDING') return 'stayPending'
    if (
        input.editorRole === 'self' &&
        input.requiresApproval &&
        (input.datesChanged || input.policyChanged)
    ) {
        return 'revertToPending'
    }
    return 'apply'
}
